import React from "react";
import { motion } from "framer-motion";
import { Calendar, Car, Truck, Sparkles, Droplets, CheckCircle } from "lucide-react";
import { Header, Footer } from "../components/common";
import { BookingModal } from "../components/booking";
import { useBooking, PACKAGES, ADDONS } from "../context";
import { Button } from "../components/ui/button";

const PackagesPage = () => {
  const { vehicleType, setVehicleType, setSelectedPackage, setShowBookingModal } = useBooking();

  const handleBookPackage = (packageKey) => {
    setSelectedPackage(packageKey);
    setShowBookingModal(true);
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="packages-page">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="gradient-text">Packages</span>
            </h1>
            <p className="text-slate-400 text-lg mb-8">
              Professional mobile car detailing packages tailored to your needs
            </p>
            
            {/* Vehicle Selector */}
            <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-12">
              {[
                { id: "small", label: "Small Car", icon: Car },
                { id: "medium", label: "SUV / Med", icon: Car },
                { id: "large", label: "Large / Van", icon: Truck },
                { id: "xl", label: "XL / Truck", icon: Truck }
              ].map((v) => (
                <button
                  key={v.id}
                  onClick={() => setVehicleType(v.id)}
                  className={`vehicle-selector-btn flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all ${
                    vehicleType === v.id ? "active" : ""
                  }`}
                  data-testid={`vehicle-${v.id}-btn`}
                >
                  <v.icon className="w-5 h-5" />
                  {v.label}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Packages Detail */}
      <section className="pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-8">
            {Object.entries(PACKAGES).map(([key, pkg], index) => {
              const price = pkg.basePrices[vehicleType] || pkg.basePrices.small;
              
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`glass rounded-2xl overflow-hidden ${pkg.popular ? "ring-2 ring-cyan-400" : ""}`}
                  data-testid={`package-${key}`}
                >
                  {pkg.popular && (
                    <div className="bg-gradient-to-r from-cyan-400 to-teal-500 text-slate-900 text-center py-2 text-sm font-bold">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                      <div>
                        <h2 className="text-2xl md:text-3xl font-bold mb-2">{pkg.name}</h2>
                        {pkg.includes && (
                          <p className="text-cyan-400 text-sm mb-2">{pkg.includes}</p>
                        )}
                        <p className="text-slate-400">{pkg.tagline}</p>
                      </div>
                      <div className="mt-4 md:mt-0 text-right">
                        <div className="text-4xl md:text-5xl font-black gradient-text">${price}</div>
                        <p className="text-slate-500 text-sm">{vehicleType === 'small' ? 'Small car' : vehicleType === 'medium' ? 'SUV/Medium' : vehicleType === 'large' ? 'Large/Van' : 'XL/Truck'}</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 mb-6">
                      <div>
                        <h3 className="font-bold text-cyan-400 mb-4 flex items-center gap-2">
                          <Sparkles className="w-5 h-5" />
                          Interior Inclusions
                        </h3>
                        <ul className="space-y-2">
                          {pkg.interior.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-300">
                              <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h3 className="font-bold text-cyan-400 mb-4 flex items-center gap-2">
                          <Droplets className="w-5 h-5" />
                          Exterior Inclusions
                        </h3>
                        <ul className="space-y-2">
                          {pkg.exterior.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-300">
                              <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-slate-700">
                      <Button
                        onClick={() => handleBookPackage(key)}
                        className={`flex-1 rounded-full py-3 font-semibold ${
                          pkg.popular 
                            ? "btn-primary-glow text-slate-900" 
                            : "bg-slate-700 hover:bg-slate-600 text-white"
                        }`}
                        data-testid={`book-${key}-btn`}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Book This Package
                      </Button>
                    </div>

                    <p className="text-xs text-slate-500 mt-4 text-center">
                      *Heavy duty cleaning may include additional charges
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Add-ons Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">
              Optional <span className="gradient-text">Add-ons</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {ADDONS.map((addon) => (
                <div key={addon.id} className="glass rounded-xl p-4 text-center" data-testid={`addon-${addon.id}`}>
                  <p className="font-medium text-slate-300 mb-2">{addon.name}</p>
                  <p className="text-cyan-400 font-bold text-xl">+${addon.price}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BookingModal />
    </div>
  );
};

export default PackagesPage;
